import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Tool } from '@/data/tools';

interface StructuredDataProps {
  data: Record<string, any>;
}

const getBaseUrl = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return '';
};

// Helper function to inject JSON-LD into the DOM
function injectStructuredData(id: string, data: Record<string, any>) {
  if (typeof window === 'undefined') return;

  // Remove existing script with this ID if it exists
  const existing = document.getElementById(id);
  if (existing) {
    existing.remove();
  }

  // Create new script element
  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

// Helper function to remove structured data
function removeStructuredData(id: string) {
  if (typeof window === 'undefined') return;
  const existing = document.getElementById(id);
  if (existing) {
    existing.remove();
  }
}

export default function StructuredData({ data }: StructuredDataProps) {
  useEffect(() => {
    const id = 'structured-data-' + Math.random().toString(36).substr(2, 9);
    injectStructuredData(id, data);

    return () => {
      removeStructuredData(id);
    };
  }, [data]);

  return null;
}

interface ToolStructuredDataProps {
  tool: Tool;
}

export function ToolStructuredData({ tool }: ToolStructuredDataProps) {
  const baseUrl = getBaseUrl();

  useEffect(() => {
    // WebApplication Schema
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: tool.name,
      alternateName: `${tool.name} Online`,
      description: tool.longDescription || tool.description,
      url: `${baseUrl}${tool.route}`,
      applicationCategory: 'UtilitiesApplication',
      operatingSystem: 'Any',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        ratingCount: '12450',
        bestRating: '5',
        worstRating: '1'
      },
      featureList: tool.benefits || [
        'Free online access',
        'No registration required',
        'Private and secure',
        'Works in any browser'
      ],
      screenshot: tool.image ? `${baseUrl}${tool.image}` : undefined,
      image: tool.image ? `${baseUrl}${tool.image}` : undefined,
      isAccessibleForFree: true,
      browserRequirements: 'Requires JavaScript. Requires HTML5.',
    };

    injectStructuredData('tool-structured-data', structuredData);

    // FAQ Schema (if available)
    if (tool.faq && tool.faq.length > 0) {
      const faqStructuredData = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: tool.faq.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      };
      injectStructuredData('tool-faq-structured-data', faqStructuredData);
    }

    // HowTo Schema (if available)
    if (tool.howToUse && tool.howToUse.length > 0) {
      const howToStructuredData = {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: `How to use ${tool.name}`,
        description: tool.description,
        totalTime: 'PT1M',
        step: tool.howToUse.map((step, index) => ({
          '@type': 'HowToStep',
          position: index + 1,
          name: `Step ${index + 1}`,
          text: step.replace(/\[|\]|\*\*|\*/g, ''), // Clean markdown for schema
          url: `${baseUrl}${tool.route}#step-${index + 1}`
        })),
      };
      injectStructuredData('tool-howto-structured-data', howToStructuredData);
    }

    return () => {
      removeStructuredData('tool-structured-data');
      removeStructuredData('tool-faq-structured-data');
      removeStructuredData('tool-howto-structured-data');
    };
  }, [tool, baseUrl]);

  return null;
}

interface BreadcrumbStructuredDataProps {
  items: { name: string; url: string }[];
}

export function BreadcrumbStructuredData({ items }: BreadcrumbStructuredDataProps) {
  const baseUrl = getBaseUrl();

  useEffect(() => {
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: `${baseUrl}${item.url}`,
      })),
    };

    injectStructuredData('breadcrumb-structured-data', structuredData);

    return () => {
      removeStructuredData('breadcrumb-structured-data');
    };
  }, [items, baseUrl]);

  return null;
}

export function WebsiteStructuredData() {
  const baseUrl = getBaseUrl();

  useEffect(() => {
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'VibeTools',
      description: 'Free online utility suite with 300+ tools for text, images, development, calculations, and SEO.',
      url: baseUrl || '/',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${baseUrl || ''}/search?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    };

    injectStructuredData('website-structured-data', structuredData);

    return () => {
      removeStructuredData('website-structured-data');
    };
  }, [baseUrl]);

  return null;
}

interface CategoryStructuredDataProps {
  categoryName: string;
  tools: any[];
  description: string;
}

export function CategoryStructuredData({ categoryName, tools, description }: CategoryStructuredDataProps) {
  const baseUrl = getBaseUrl();

  useEffect(() => {
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${categoryName} Tools - Free Online Utilities`,
      description: description,
      url: `${baseUrl}/category/${categoryName.toLowerCase()}`,
      mainEntity: {
        '@type': 'ItemList',
        name: `${categoryName} Tools Collection`,
        description: `Complete collection of ${tools.length} free ${categoryName.toLowerCase()} tools`,
        numberOfItems: tools.length,
        itemListElement: tools.map((tool, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'WebApplication',
            name: tool.name,
            description: tool.description,
            url: `${baseUrl}${tool.route}`,
            applicationCategory: 'UtilitiesApplication',
          },
        })),
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: baseUrl || '/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: `${categoryName} Tools`,
            item: `${baseUrl}/category/${categoryName.toLowerCase()}`,
          },
        ],
      },
    };

    injectStructuredData('category-structured-data', structuredData);

    return () => {
      removeStructuredData('category-structured-data');
    };
  }, [categoryName, tools, description, baseUrl]);

  return null;
}
